import { ReloadOutlined } from '@ant-design/icons';
import { getHumanReadableTaxValue } from '@mahawi/eshop-common/src/get-human-readable-tax-value';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import { type ICountry } from '@mahawi/eshop-common/src/types';
import { Button, Divider, Space, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { ColumnFilterItem } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import Table from '../components/table';
import UpdatedAt from '../components/updated-at';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import { countriesLoadAll } from '../reducers/countries/reducer';
import { SPACING } from '../theme/tokens';

interface DataType extends ICountry {
  key: string;
}

function SettingsCountriesContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const countriesCountries = useSelector(
    (s: RootState) => s.Countries.countries,
  );
  const countriesInProcess = useSelector(
    (s: RootState) => s.Countries.inProcess,
  );
  const countriesUpdatedAt = useSelector(
    (s: RootState) => s.Countries.updatedAt,
  );

  const [barElement, setBarElement] = useState<
    React.ReactElement | undefined
  >();
  const [columns, setColumns] = useState<ColumnsType<object>>([]);
  const [dataSource, setDataSource] = useState<object[]>([]);

  useEffect((): void => {
    const barElementUE: React.ReactElement = (
      <Space
        size={SPACING.lg}
        align="center"
        separator={<Divider orientation="vertical" />}
      >
        <Button
          type={!countriesCountries ? 'primary' : 'default'}
          onClick={(): void => {
            dispatch(countriesLoadAll());
          }}
          disabled={countriesInProcess}
        >
          Reload all <ReloadOutlined />
        </Button>

        <UpdatedAt value={countriesUpdatedAt} />
      </Space>
    );

    setBarElement(barElementUE);
  }, [countriesInProcess, countriesUpdatedAt, dispatch, countriesCountries]);

  useEffect((): void => {
    const dataSourceUE: DataType[] | undefined = countriesCountries?.map(
      (country: ICountry): DataType => ({
        ...country,
        key: country.isoCode,
      }),
    );

    const columnsUE: ColumnsType<object> = [
      {
        title: 'Name',
        dataIndex: 'name',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.name, b.name),
        filterSearch: true,
        filters: [
          ...new Set(dataSourceUE?.map(({ name }: DataType): string => name)),
        ].map((name: string): ColumnFilterItem => ({
          text: name,
          value: name,
        })),
        onFilter: (value: string, record: DataType): boolean =>
          record.name === value,
        render: (text: string, record: DataType): React.ReactElement => (
          <Link
            to={`/settings/country/${record.isoCode}`}
            label={text}
            openInNewTab
          />
        ),
        defaultSortOrder: 'ascend',
      },
      {
        title: 'ISO code',
        dataIndex: 'isoCode',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.isoCode, b.isoCode),
        filterSearch: true,
        filters: [
          ...new Set(
            dataSourceUE?.map(({ isoCode }: DataType): string => isoCode),
          ),
        ].map((isoCode: string): ColumnFilterItem => ({
          text: isoCode,
          value: isoCode,
        })),
        onFilter: (value: string, record: DataType): boolean =>
          record.isoCode === value,
      },
      {
        title: 'ISO2 code',
        dataIndex: 'isoCode2',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.isoCode2, b.isoCode2),
        filterSearch: true,
        filters: [
          ...new Set(
            dataSourceUE?.map(({ isoCode2 }: DataType): string => isoCode2),
          ),
        ].map((isoCode2: string): ColumnFilterItem => ({
          text: isoCode2,
          value: isoCode2,
        })),
        onFilter: (value: string, record: DataType): boolean =>
          record.isoCode2 === value,
      },
      {
        title: 'VAT rate',
        dataIndex: 'vatRate',
        sorter: (a: DataType, b: DataType): number =>
          numberCompare(a.vatRate, b.vatRate),
        render: (vatRate: number): React.ReactElement => (
          <Typography.Text strong>
            {getHumanReadableTaxValue(vatRate)}
          </Typography.Text>
        ),
      },
      {
        title: 'Active',
        dataIndex: 'isActive',
        sorter: (a: DataType, b: DataType): number => {
          if (a.isActive === b.isActive) {
            return 0;
          }

          return a.isActive ? -1 : 1;
        },
        filterSearch: true,
        filters: [
          { text: 'Active', value: true },
          { text: 'Inactive', value: false },
        ],
        onFilter: (value: boolean, record: DataType): boolean =>
          record.isActive === value,
        render: (isActive: boolean): React.ReactElement =>
          isActive ? (
            <Typography.Text strong type="success">
              Active
            </Typography.Text>
          ) : (
            <Typography.Text strong type="warning">
              Deactivated
            </Typography.Text>
          ),
      },
    ];

    setColumns(columnsUE);

    if (!dataSourceUE) {
      return;
    }

    setDataSource(dataSourceUE);
  }, [countriesCountries, dispatch]);

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader title="Countries" />

      {barElement}

      <Table
        columns={columns}
        dataSource={dataSource}
        isLoading={countriesInProcess && !countriesCountries}
      />
    </Space>
  );
}

export default SettingsCountriesContainer;
